import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const roles = [
  {
    title: 'HR & Behavioral',
    description: 'Automate the first-round screen. Assess culture fit, communication, and core competencies with human-like conversational depth.',
    icon: 'Users',
    color: 'emerald',
    features: ['Culture fit assessment', 'Resume deep-dives', 'Soft skills scoring'],
  },
  {
    title: 'Managerial & Leadership',
    description: 'Evaluate strategic thinking and conflict resolution. The AI role-plays complex management scenarios to test leadership under pressure.',
    icon: 'Briefcase',
    color: 'sky',
    features: ['Scenario-based roleplay', 'Conflict resolution', 'Strategic alignment'],
  },
  {
    title: 'Sales & Client-Facing',
    description: 'Run realistic mock pitches and objection handling exercises. Assess confidence, articulation, and product positioning in real-time.',
    icon: 'TrendingUp',
    color: 'amber',
    features: ['Mock sales pitches', 'Objection handling', 'Persuasion metrics'],
  },
  {
    title: 'Technical (DSA)',
    description: 'Watch candidates solve algorithmic problems live. The AI interviewer provides hints, analyzes complexity, and evaluates coding proficiency.',
    icon: 'Code',
    color: 'indigo',
    features: ['Live coding environment', 'Big-O analysis', 'Algorithmic efficiency'],
  },
  {
    title: 'System Design',
    description: 'Assess architectural knowledge. Evaluate how candidates design scalable, fault-tolerant systems with real-world constraints.',
    icon: 'Server',
    color: 'rose',
    features: ['Architecture design', 'Scalability testing', 'Constraint handling'],
  },
];

const colorClasses = {
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', border: 'border-emerald-500/20' },
  sky: { bg: 'bg-sky-500/10', text: 'text-sky-500', border: 'border-sky-500/20' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/20' },
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-500', border: 'border-indigo-500/20' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-500', border: 'border-rose-500/20' },
};

const RoleCard = ({ role, idx }) => {
  const colors = colorClasses[role.color];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: idx * 0.08 }}
      className="group flex h-full flex-col rounded-2xl border border-[var(--lp-line)] bg-white p-6 transition-colors duration-300 hover:border-[var(--lp-accent)]/40"
    >
      <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-xl border ${colors.bg} ${colors.border}`}>
        <Icon name={role.icon} size={24} className={colors.text} />
      </div>

      <h3 className="mb-3 text-xl text-[var(--lp-ink)]">{role.title}</h3>

      <p className="mb-6 flex-grow text-sm leading-relaxed text-[var(--lp-ink-soft)]">
        {role.description}
      </p>

      <div className="mt-auto space-y-2 border-t border-[var(--lp-line)] pt-4">
        {role.features.map((feature) => (
          <div key={feature} className="flex items-center gap-2 text-sm text-[var(--lp-ink)]">
            <Icon name="CheckCircle2" size={14} className={colors.text} />
            {feature}
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const BusinessRolesSection = () => {
  const topRow = roles.slice(0, 3);
  const bottomRow = roles.slice(3);

  return (
    <section id="roles" className="relative z-10 border-t border-[var(--lp-line)] bg-white py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <p className="font-mono text-[11px] font-semibold tracking-[0.2em] text-[var(--lp-accent)]">
            Interview types
          </p>
          <h2 className="mb-4 mt-4 text-3xl tracking-tight text-[var(--lp-ink)] md:text-4xl">
            One platform for <span className="text-[var(--lp-accent)]">every role.</span>
          </h2>
          <p className="text-lg leading-relaxed text-[var(--lp-ink-soft)]">
            From technical deep-dives to behavioral screening, prepxai adapts its interview style to evaluate the exact skills you need.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {topRow.map((role, idx) => (
            <RoleCard key={role.title} role={role} idx={idx} />
          ))}
        </div>

        <div className="mx-auto mt-6 grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
          {bottomRow.map((role, idx) => (
            <RoleCard key={role.title} role={role} idx={idx + 3} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BusinessRolesSection;
